import { Box, Flex, Text, TextInput } from "@mantine/core";
import { IconDownload, IconFileTypePdf, IconUpload, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button } from "components";
import { ChevronSelect } from "pages/todo/list/new/DateTimeSelect";
import { DateUtils } from "utils";

import StatusIndicator from "./StatusIndicator";
import { QuickCheckDocument, QuickCheckStatus } from "./types";

interface QuickCheckDocumentRowProps {
  document: QuickCheckDocument;
  onStatusChange?: (status: QuickCheckStatus) => void;
  onPointsChange?: (points: number | undefined) => void;
  onBlockedUntilChange?: (date: string | undefined) => void;
  onUpload?: () => void;
  onDownload?: () => void;
  onView?: () => void; // Otvara dokument u novom tabu
  onDelete?: () => void;
  isAdmin?: boolean;
}

const getFileExtensionLabel = (extension: string | undefined): string => {
  if (!extension) return "";
  return extension.toUpperCase();
};

const formatBlockedUntil = (dateStr: string | undefined): string => {
  if (!dateStr) return "---";
  return DateUtils.formatDateDisplay(dateStr) || dateStr;
};

const formatCreatedAt = (dateStr: string | undefined): string => {
  if (!dateStr) return "---";
  return DateUtils.formatDateDisplay(dateStr) || "---";
};

const QuickCheckDocumentRow = ({
  document,
  onStatusChange,
  onPointsChange,
  onBlockedUntilChange,
  onUpload,
  onDownload,
  onView,
  onDelete,
  isAdmin = true,
}: QuickCheckDocumentRowProps) => {
  const { t } = useTranslation();

  // Dokument postoji ako ima ID (vraćen od BE-a)
  const hasFile = !!document.id;
  const showPoints = document.type === "KRIF";
  // Generate day options (1-31)
  const dayOptions = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: String(i + 1).padStart(2, "0"),
  }));

  // Generate month options (1-12)
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: String(i + 1).padStart(2, "0"),
  }));

  // Generate year options (current year + next year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 2 }, (_, i) => ({
    value: String(currentYear + i),
    label: String(currentYear + i),
  }));

  // When no file is uploaded, show simplified row
  if (!hasFile) {
    return (
      <Box>
        <Flex align="center" gap="md">
          <Text fw={600} size="lg">
            {t(`quickCheck.documents.${document.type.toLowerCase()}`)}
          </Text>
          {isAdmin ? (
            <Button
              color="primary"
              radius="xl"
              size="sm"
              leftSection={<IconUpload size={18} />}
              onClick={onUpload}
            >
              {t("quickCheck.uploadDocument")}
            </Button>
          ) : (
            <Text size="sm" c="gray.6">
              {t("quickCheck.notAttached")}
            </Text>
          )}
        </Flex>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header row: Document type + Status + Column headers */}
      <Flex align="center" mb="sm">
        <Flex align="center" gap="md" mr="md" style={{ minWidth: 160 }}>
          <Text fw={600} size="lg">
            {t(`quickCheck.documents.${document.type.toLowerCase()}`)}
          </Text>
          {isAdmin ? (
            <StatusIndicator
              status={document.status}
              withDropdown={true}
              onStatusChange={onStatusChange}
            />
          ) : (
            <StatusIndicator status={document.status} />
          )}
        </Flex>

        {/* Column headers */}
        <Text c="gray.6" size="sm" style={{ width: 110 }}>
          {t("quickCheck.fields.administrator")}
        </Text>
        <Text c="gray.6" size="sm" style={{ width: 100 }}>
          {t("quickCheck.fields.attachedAt")}
        </Text>
        <Text c="gray.6" size="sm" style={{ flex: 1 }}>
          {showPoints ? t("quickCheck.fields.points") : t("quickCheck.fields.blockedUntil")}
        </Text>
        <Box ml="md" style={{ width: 80 }} />
      </Flex>

      {/* Content row: File info + Admin + Date + Points/BlockedUntil + Actions */}
      <Flex align="center">
        {/* File info - clickable to open document in new tab */}
        <Flex
          align="center"
          gap="sm"
          mr="md"
          style={{ minWidth: 160, cursor: "pointer" }}
          onClick={onView}
        >
          <IconFileTypePdf size={40} color="var(--mantine-color-primary-6)" />
          <Box>
            <Text size="sm">{document.fileName}</Text>
            <Text size="xs" c="gray.6">
              {getFileExtensionLabel(document.extension)}
            </Text>
          </Box>
        </Flex>

        {/* Administrator */}
        <Text size="sm" style={{ width: 110 }}>
          {document.createdByName || "---"}
        </Text>

        {/* Attached date */}
        <Text size="sm" style={{ width: 100 }}>
          {formatCreatedAt(document.createdAt)}
        </Text>

        {/* Points or Blocked Until */}
        <Box style={{ flex: 1 }}>
          {showPoints ? (
            isAdmin ? (
              <TextInput
                placeholder=""
                value={document.points?.toString() || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  onPointsChange?.(val ? parseInt(val, 10) : undefined);
                }}
                style={{ width: 80 }}
              />
            ) : (
              <Text size="sm">{document.points ?? "---"}</Text>
            )
          ) : isAdmin ? (
            <Flex gap="xs">
              <ChevronSelect
                placeholder="DD"
                data={dayOptions}
                value={document.blockedUntil?.split("-")[2] || null}
                onChange={(newDay) => {
                  const [year, month] = (document.blockedUntil || "--").split("-");
                  onBlockedUntilChange?.(`${year || ""}-${month || ""}-${newDay || ""}`);
                }}
                size="xs"
                w={60}
              />
              <ChevronSelect
                placeholder="MM"
                data={monthOptions}
                value={document.blockedUntil?.split("-")[1] || null}
                onChange={(newMonth) => {
                  const parts = (document.blockedUntil || "--").split("-");
                  onBlockedUntilChange?.(`${parts[0] || ""}-${newMonth || ""}-${parts[2] || ""}`);
                }}
                size="xs"
                w={60}
              />
              <ChevronSelect
                placeholder="YYYY"
                data={yearOptions}
                value={document.blockedUntil?.split("-")[0] || null}
                onChange={(newYear) => {
                  const parts = (document.blockedUntil || "--").split("-");
                  onBlockedUntilChange?.(`${newYear || ""}-${parts[1] || ""}-${parts[2] || ""}`);
                }}
                size="xs"
                w={75}
              />
            </Flex>
          ) : (
            <Text size="sm">{formatBlockedUntil(document.blockedUntil)}</Text>
          )}
        </Box>

        {/* Actions - only for admin */}
        {isAdmin && (
          <Flex gap="sm" ml="md" style={{ width: 80 }}>
            <Button variant="transparent" size="compact-xs" onClick={onDownload}>
              <IconDownload size={22} color="var(--mantine-color-primary-6)" />
            </Button>
            <Button variant="transparent" size="compact-xs" onClick={onDelete}>
              <IconX size={22} color="var(--mantine-color-red-6)" />
            </Button>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default QuickCheckDocumentRow;
